import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OT, StatutOT, TypeOT } from '../../models/agenda/ot.model';
import { Events } from '../../providers/events/events.provider';
import { AgendaGridUtils } from '../../utils/grid-utils';
import { IonicModule } from '@ionic/angular';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import { ChronosProvider } from '../../providers/chronos/chronos.provider';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChronoActiviteService } from '../../providers/chrono-activite/chrono-activite.service';
import { ActiviteEnCoursService } from '../activite-en-cours/activite-en-cours.service';

/**
 * Taille en pixel définissant la hauteur maximale d'une plage avec un style compact
 */
const compactHeight = 67;

/**
 * OT card de l'agenda
 */
@Component({
  selector: 'ot-card',
  templateUrl: 'ot-card.component.html',
  styleUrls: ['ot-card.component.scss'],
  standalone: true,
  imports: [IonicModule, DatePipe, NgClass, CommonModule],
})
export class OtCardComponent implements OnInit, OnDestroy, OnChanges {
  private readonly chronoActiviteService: ChronoActiviteService = inject(ChronoActiviteService);
  private readonly activiteEnCoursService: ActiviteEnCoursService = inject(ActiviteEnCoursService);
  private readonly events = inject(Events);
  private readonly chronosProvider = inject(ChronosProvider);
  private readonly translate = inject(TranslateService);
  // importation de l'enum à la vue
  StatutOt = StatutOT;
  TypeOt = TypeOT;

  /**
   * OT à afficher
   */
  @Input() ot: OT;

  @Input() nbrOtsSite: number = 0;

  /**
   * Position sur la grille
   */
  @Input() position: number;

  /**
   * Nombre d'OT chevauchants
   */
  @Input() numberSideBySide: number;

  /**
   * Si la card est utilisée dans le day grid
   */
  @Input() isInDayGrid: boolean;

  /**
   * Affichage des icones et labels dans la card
   */
  @Input() displayIconsLabels = true;

  /**
   * Au clic on fait remonter l'ot pour alimenter le boutton flottant d'interaction avec l'OT
   */
  @Output() interactedOT: EventEmitter<OT> = new EventEmitter<OT>();

  /**
   * Placement haut de l'OT
   */
  @HostBinding('style.top.%') top: number;

  /**
   * Placement bas de l'OT
   */
  @HostBinding('style.bottom.%') bottom: number;

  /**
   * Placement gauche de l'OT
   */
  @HostBinding('style.left.%') left: number;

  /**
   * Placement droit de l'OT
   */
  @HostBinding('style.right.%') right: number;

  /**
   * OT Card
   */
  @ViewChild('otCard') otCard: ElementRef;

  /**
   * Liste des icones
   */
  icons: String[] = [];

  /**
   * Liste des labels
   */
  labels: String[] = [];

  /**
   * Définit si l'OT est à afficher en mode compact
   */
  isCompact: boolean;

  private refreshChrono: () => void;

  hasActiviteEnPause: boolean = false;

  /**
   * Description du site à afficher
   */
  descriptionSite = '';

  constructor() {
    effect(() => {
      this.hasActiviteEncours();
      this.updateIconsAndLabels();
    });
  }

  /**
   * ngOnInit
   */
  async ngOnInit(): Promise<void> {
    if (this.isInDayGrid) {
      let largeur = 100 / this.numberSideBySide;
      this.top = AgendaGridUtils.dateToVerticalPercent(this.ot.dateDebut);
      this.bottom = 100 - AgendaGridUtils.dateToVerticalPercent(this.ot.dateFin);
      this.left = this.position * largeur;
      this.right = 100 - (largeur + this.left);
    }
    this.hasActiviteEnPause = await this.activiteEnCoursService.getActiviteEnPauseExists(this.ot.id);
    this.setDescriptionSite();
    this.updateIconsAndLabels();

    this.refreshChrono = () => this.updateIconsAndLabels();
    this.events.subscribe('refreshTab', this.refreshChrono);
  }

  /**
   * ngOnChanges
   * @param changes SimpleChange
   */
  ngOnChanges(changes: SimpleChanges) {
    if (!changes['ot'].isFirstChange()) {
      this.setDescriptionSite();
      this.updateIconsAndLabels();
    }
  }

  /**
   * Affiche les libellés du chrono en mode compact
   */
  get displayLabelsCompact() {
    this.isCompact = this.otCard?.nativeElement?.offsetHeight < compactHeight;
    return this.isCompact && this.displayIconsLabels && this.isInDayGrid;
  }

  /**
   * Retourne vrai si l'OT est clos valide ou intervenant
   */
  get isOtClos(): boolean {
    return this.ot.statut === this.StatutOt.ClosValide || this.ot.statut === this.StatutOt.ClosIntervenant;
  }

  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    if (this.refreshChrono) {
      this.events.unsubscribe('refreshTab', this.refreshChrono);
    }
  }

  setDescriptionSite(): void {
    if (this.ot && this.ot.site) {
      this.descriptionSite = this.ot.site.libelle_long;
    }
  }

  interactWithOT() {
    if (this.isInDayGrid) {
      this.interactedOT.emit(this.ot);
    }
  }

  private readonly chronoEnCours = toSignal(this.chronoActiviteService.chronoActiviteEnCours$);
  private readonly activiteEncours = toSignal(this.activiteEnCoursService.activiteEnCours$);

  private readonly hasActiviteEncours = computed(() => {
    return this.activiteEncours() !== null && this.activiteEncours().id === this.ot.id;
  });

  readonly etatActivite = computed(() => {
    if (this.activiteEncours().id === this.ot.id && this.chronoEnCours().idActivite === this.ot.id) {
      return this.chronoEnCours().horodatageEncours.statutActivite;
    }
    return undefined;
  });

  private updateIconsAndLabels() {
    this.icons = [];
    this.labels = [];
    if (this.ot.has_echapetbelle) {
      this.icons.push('echapetbelle');
    }
    if (this.ot.isControleReglementaire) {
      this.icons.push('agenda-controle-reglementaire');
    }
    if (this.ot.isMultiIntervenant) {
      this.icons.push('agenda-multi-intervenant');
    }
    if (this.ot.isRdvClient) {
      this.icons.push('today-outline');
    }
    if (this.hasActiviteEnPause) {
      this.icons.push('hourglass-outline');
    }
    if (this.ot.isTravailRestant) {
      this.labels.push(this.translate.instant('AGENDA.OT.LABELS.TRAVAIL_RESTANT'));
    }
    if (this.isOtClos) {
      this.labels.push(this.translate.instant('AGENDA.OT.LABELS.CLOS'));
    }
    const chronoLabel = this.getChronoLabel();
    if (chronoLabel) {
      this.labels.push(chronoLabel);
    }

    if (this.isInDayGrid && this.icons.length > 2) {
      this.icons = ['agenda-more'];
    }

    if (this.hasActiviteEncours()) {
      this.labels.push(this.etatActivite());
    }
  }

  private getChronoLabel(): string {
    if (this.ot.site) {
      const index = this.chronosProvider.checkChronoExist(this.ot.id, this.ot.site.id);

      if (index >= 0) {
        const chrono = this.chronosProvider.getChronos()[index];
        if (chrono.active_travel) {
          return this.translate.instant('AGENDA.OT.LABELS.DEPLACEMENT');
        }
        if (chrono.active_intervention) {
          return this.translate.instant('AGENDA.OT.LABELS.INTERVENTION');
        }
        if (!chrono.active_travel && !chrono.active_intervention && !chrono.active_return_travel) {
          return this.translate.instant('AGENDA.OT.LABELS.PAUSE');
        }
      }
    }
    return undefined;
  }

  protected readonly TypeOT = TypeOT;
}
